// Log sent / Edit sent record (workflow D1, mockup proposals-desktop-form /
// proposals-mobile-form). One form for the Stats band and the project and
// lead rails: a modal on desktop, a full-height sheet at ≤880px (Modal's
// own mobile branch). Clients, leads and projects load lazily on open.

import { useEffect, useMemo, useState } from 'react';
import { WonProjectAction } from './WonProjectAction';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { melbourneToday } from '../../shared/dates';
import {
  LEAD_OPEN_STATUSES,
  LEAD_STATUS_LABELS,
  type ClientWithCounts,
  type DocumentSendKind,
  type DocumentSendListItem,
  type DocumentSendOutcome,
  type LeadStatus,
  type LeadWithNames,
  type ProjectWithClient,
} from '../../shared/types';
import {
  DateField,
  formatProjectNumber,
  Modal,
  parseMoney,
  Seg,
  SelectField,
  type DropdownItem,
} from './primitives';

const KIND_OPTIONS = [
  { value: 'proposal', label: 'Proposal' },
  { value: 'quote', label: 'Quote' },
] as const;

const OUTCOME_OPTIONS = [
  { value: 'pending', label: 'Awaiting reply' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'on_hold', label: 'On hold' },
] as const;

/** Lead stages that "Move the lead to Proposal sent" can advance from. */
const ADVANCEABLE: readonly LeadStatus[] = ['enquiry', 'discovery_call', 'proposal_creation'];

const NO_LINK = 'none';

export interface DocumentSendDefaults {
  kind: DocumentSendKind;
  lead_id: string;
  project_id: string;
  client_id: string;
}

/** Cents → the plain "12460" / "12460.50" an f-input money field edits. */
function centsDraft(cents: number | null): string {
  if (cents === null) return '';
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

export function DocumentSendForm({
  send = null,
  defaults,
  onClose,
}: {
  send?: DocumentSendListItem | null;
  defaults?: Partial<DocumentSendDefaults>;
  onClose: () => void;
}) {
  const editing = send !== null;
  const [kind, setKind] = useState<DocumentSendKind>(send?.kind ?? defaults?.kind ?? 'quote');
  const [ref, setRef] = useState(send?.ref ?? '');
  const [title, setTitle] = useState(send?.title ?? '');
  const [clientId, setClientId] = useState<string | null>(send ? send.client_id : defaults?.client_id ?? null);
  const [initialLink] = useState<string>(() => {
    const leadId = send ? send.lead_id : defaults?.lead_id;
    const projectId = send ? send.project_id : defaults?.project_id;
    if (projectId) return `project:${projectId}`;
    if (leadId) return `lead:${leadId}`;
    return NO_LINK;
  });
  const [link, setLink] = useState<string>(initialLink);
  const [sentDate, setSentDate] = useState<string | null>(send?.sent_date ?? melbourneToday());
  const [min, setMin] = useState(centsDraft(send?.amount_min_cents ?? null));
  const [max, setMax] = useState(centsDraft(send?.amount_max_cents ?? null));
  const [depositAmount,setDepositAmount]=useState(centsDraft(send?.deposit_cents??null));
  const [wonAmount,setWonAmount]=useState(centsDraft(send?.won_amount_cents??null));
  const [outcome, setOutcome] = useState<DocumentSendOutcome>(send?.outcome ?? 'pending');
  const [advance, setAdvance] = useState(true);
  const [busy, setBusy] = useState(false);
  const [arming, setArming] = useState(false);

  const [clients, setClients] = useState<ClientWithCounts[] | null>(null);
  const [leads, setLeads] = useState<LeadWithNames[] | null>(null);
  const [projects, setProjects] = useState<ProjectWithClient[] | null>(null);

  useEffect(() => {
    let live = true;
    api<{ clients: ClientWithCounts[] }>('/api/admin/clients')
      .then((r) => live && setClients(r.clients))
      .catch(() => live && setClients([]));
    api<{ leads: LeadWithNames[] }>('/api/admin/leads')
      .then((r) => live && setLeads(r.leads))
      .catch(() => live && setLeads([]));
    api<{ projects: ProjectWithClient[] }>('/api/admin/projects')
      .then((r) => live && setProjects(r.projects))
      .catch(() => live && setProjects([]));
    return () => {
      live = false;
    };
  }, []);

  // Remove's second click must land within four seconds, as TrashAction.
  useEffect(() => {
    if (!arming) return;
    const t = setTimeout(() => setArming(false), 4000);
    return () => clearTimeout(t);
  }, [arming]);

  const clientItems = useMemo<DropdownItem<string>[]>(
    () => (clients ?? []).map((c) => ({ value: c.id, label: c.name })),
    [clients],
  );

  // Open leads and live projects; whatever the record already links to stays
  // listed even when it has since closed, so the select never reads blank.
  const linkItems = useMemo<DropdownItem<string>[]>(() => {
    const linkedLead = link.startsWith('lead:') ? link.slice(5) : null;
    const linkedProject = link.startsWith('project:') ? link.slice(8) : null;
    const items: DropdownItem<string>[] = [{ value: NO_LINK, label: 'No lead or project' }];
    for (const l of leads ?? []) {
      if (!LEAD_OPEN_STATUSES.includes(l.status) && l.id !== linkedLead) continue;
      const who = l.client_name ?? l.company;
      items.push({
        value: `lead:${l.id}`,
        label: l.name,
        sub: ['Lead', who, LEAD_STATUS_LABELS[l.status]].filter(Boolean).join(' · '),
        group: 'Leads',
      });
    }
    for (const p of projects ?? []) {
      if (p.voided_at !== null && p.id !== linkedProject) continue;
      const num = formatProjectNumber(p.project_number);
      items.push({
        value: `project:${p.id}`,
        label: num ? `${num} · ${p.name}` : p.name,
        sub: p.client_name,
        group: 'Projects',
      });
    }
    return items;
    // `link` only matters for keeping the record's own link listed.
  }, [leads, projects, link]);

  const chosenLead = link.startsWith('lead:') ? leads?.find((l) => l.id === link.slice(5)) ?? null : null;
  const showAdvance = !editing && chosenLead !== null && ADVANCEABLE.includes(chosenLead.status);

  const pickLink = (value: string) => {
    setLink(value);
    if (clientId) return;
    // Auto-fill Client from the chosen lead or project when it's still empty.
    if (value.startsWith('lead:')) {
      const lead = leads?.find((l) => l.id === value.slice(5));
      if (lead?.client_id) setClientId(lead.client_id);
    } else if (value.startsWith('project:')) {
      const project = projects?.find((p) => p.id === value.slice(8));
      if (project) setClientId(project.client_id);
    }
  };

  const minCents = min.trim() ? parseMoney(min) : null;
  const maxCents = max.trim() ? parseMoney(max) : null;
  const amountInvalid = (min.trim() !== '' && minCents === null) || (max.trim() !== '' && maxCents === null);
  const depositCents=depositAmount.trim()?parseMoney(depositAmount):null;
  const wonCents=wonAmount.trim()?parseMoney(wonAmount):null;
  const termsInvalid=(depositAmount.trim()!=='' && (depositCents===null || depositCents<=0 || depositCents>10000000)) || (wonAmount.trim()!=='' && wonCents===null);
  const rangeInvalid = minCents !== null && maxCents !== null && maxCents < minCents;
  // An unlinked record (client name only, from the proposal tool) still has
  // a client; Henry can link it to the real one but isn't forced to.
  const hasClient = clientId !== null || Boolean(send?.client_name);
  const canSave = Boolean(title.trim()) && Boolean(sentDate) && hasClient && !amountInvalid && !rangeInvalid && !termsInvalid && !busy;

  const save = async () => {
    if (!canSave) return;
    const leadId = link.startsWith('lead:') ? link.slice(5) : null;
    const projectId = link.startsWith('project:') ? link.slice(8) : null;
    const body: Record<string, unknown> = {
      kind,
      title: title.trim(),
      ref: ref.trim() || null,
      lead_id: leadId,
      project_id: projectId,
      sent_date: sentDate,
      amount_min_cents: minCents,
      amount_max_cents: maxCents,
      outcome,
      deposit_cents:depositCents, won_amount_cents:wonCents,
    };
    if (clientId !== null) body.client_id = clientId;
    setBusy(true);
    try {
      if (send) {
        // Only what changed: an untouched sent_date must not clear the
        // backfill's "date est." flag, and an untouched client must not
        // unlink a client_name-only record.
        const before: Record<string, unknown> = {
          kind: send.kind,
          title: send.title,
          ref: send.ref,
          lead_id: send.lead_id,
          project_id: send.project_id,
          sent_date: send.sent_date,
          amount_min_cents: send.amount_min_cents,
          amount_max_cents: send.amount_max_cents,
          outcome: send.outcome,
          deposit_cents:send.deposit_cents??null,won_amount_cents:send.won_amount_cents,
          client_id: send.client_id,
        };
        const changes = Object.fromEntries(Object.entries(body).filter(([k, v]) => before[k] !== v));
        // The single "lead or project" select shows only one of a send's
        // links (a proposal-tool send can carry both). Leave both alone
        // unless Henry actually changed the selection.
        if (link === initialLink) {
          delete changes.lead_id;
          delete changes.project_id;
        }
        if (changes.outcome !== undefined) changes.outcome_date = outcome === 'pending' ? null : melbourneToday();
        if (Object.keys(changes).length > 0) {
          await api(`/api/admin/document-sends/${send.id}`, { method: 'PATCH', json: changes });
        }
      } else {
        if (outcome !== 'pending') body.outcome_date = melbourneToday();
        if (showAdvance && advance) body.advance_lead = true;
        await api('/api/admin/document-sends', { method: 'POST', json: body });
      }
      onClose();
    } catch (error) {
      reportError(error, editing ? 'Could not save the sent record.' : 'Could not log the send.');
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!send) return;
    if (!arming) {
      setArming(true);
      return;
    }
    setBusy(true);
    try {
      await api(`/api/admin/document-sends/${send.id}`, { method: 'DELETE' });
      onClose();
    } catch (error) {
      reportError(error, 'Could not remove the sent record.');
      setBusy(false);
      setArming(false);
    }
  };

  return (
    <Modal
      title={editing ? 'Edit sent record' : 'Log sent'}
      context="Proposals & quotes"
      width={580}
      onClose={onClose}
      hint="Counted once per document. Revisions and resends update this entry rather than adding another."
      foot={
        <>
          {editing && (
            <button className="btn danger" disabled={busy} onClick={() => void remove()}>
              {arming ? 'Confirm remove' : 'Remove'}
            </button>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!canSave} onClick={() => void save()}>
            {editing ? 'Save' : 'Log sent'}
          </button>
        </>
      }
    >
      {send?.outcome==='won' && <div style={{marginBottom:16}}><WonProjectAction send={send}/></div>}
      <div className="form-grid">
        <div className="f-field"><label className="f-label">Won value · ex GST</label><input className="f-input money" inputMode="decimal" value={wonAmount} onChange={e=>setWonAmount(e.target.value)} placeholder="Use quote range"/></div>
        <div className="f-field"><label className="f-label">Deposit terms · ex GST (optional)</label><input className="f-input money" inputMode="decimal" value={depositAmount} onChange={e=>setDepositAmount(e.target.value)} placeholder="No deposit"/>{termsInvalid&&<p className="error-text">Enter valid amounts; deposit must be positive and at most $100,000.</p>}</div>
        <div className="f-field">
          <span className="f-label">Kind</span>
          <Seg options={KIND_OPTIONS} value={kind} onChange={setKind} />
        </div>
        <div className="f-field">
          <span className="f-label">Reference</span>
          <input
            className="f-input mono"
            placeholder={kind === 'quote' ? 'SS-2026-0154' : 'optional'}
            value={ref}
            onChange={(e) => setRef(e.target.value)}
          />
        </div>
        <div className="f-field span-2">
          <span className="f-label">
            Title <span className="req">*</span>
          </span>
          <input
            className="f-input"
            placeholder="e.g. Clinic welcome series"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="f-field">
          <span className="f-label">
            Client <span className="req">*</span>
          </span>
          <SelectField
            searchable
            label="Client"
            placeholder={clientId === null && send?.client_name ? send.client_name : 'Choose a client…'}
            items={clientItems}
            value={clientId}
            onChange={setClientId}
          />
        </div>
        <div className="f-field">
          <span className="f-label">Lead or project</span>
          <SelectField searchable label="Lead or project" items={linkItems} value={link} onChange={pickLink} />
        </div>
        <div className="f-field">
          <span className="f-label">
            Sent date <span className="req">*</span>
          </span>
          <DateField value={sentDate} onChange={setSentDate} placeholder="Sent date" />
        </div>
        <div className="f-field">
          <span className="f-label">Amount · ex GST</span>
          <div className="amount-pair">
            <input
              className="f-input money"
              inputMode="decimal"
              placeholder="12,460"
              value={min}
              onChange={(e) => setMin(e.target.value)}
            />
            <span className="sep">to</span>
            <input
              className="f-input money"
              inputMode="decimal"
              placeholder="optional"
              value={max}
              onChange={(e) => setMax(e.target.value)}
            />
          </div>
          {amountInvalid && <span className="f-help">Amounts must be numbers</span>}
          {!amountInvalid && rangeInvalid && <span className="f-help">The upper amount can't be below the lower</span>}
        </div>
        <div className="f-field span-2">
          <span className="f-label">Outcome</span>
          <Seg options={OUTCOME_OPTIONS} value={outcome} onChange={setOutcome} />
        </div>
        {showAdvance && (
          <label className="ft-check span-2">
            <input type="checkbox" checked={advance} onChange={(e) => setAdvance(e.target.checked)} />
            Move the lead to Proposal sent
          </label>
        )}
      </div>
    </Modal>
  );
}
